import { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import {
  useFolderTemplates,
  useFolderTemplate,
  useCreateFolderTemplate,
  useUpdateFolderTemplate,
  useDeleteFolderTemplate,
  type FolderTemplate,
  type FolderTemplateNodePayload,
  type FolderTemplateTreeNode,
} from './hooks/useFolderTemplates';

/**
 * FolderTemplatesPage — define reusable document folder structures.
 *
 * Templates are named trees of folder names. A template can be marked as the
 * tenant default (applied to new contacts). Assigning a template to a contact
 * materializes the folders under that client and is mirrored in object storage
 * and on the client portal.
 */
export function FolderTemplatesPage() {
  const { can } = usePermissions();
  const { toast } = useToast();
  const { data: templates, isLoading } = useFolderTemplates({ includeInactive: true });
  const deleteMutation = useDeleteFolderTemplate();

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const canManage = can(PERMISSIONS.DOCUMENTS_MANAGE_FOLDERS);

  const handleCreate = () => {
    setEditingId(null);
    setIsEditorOpen(true);
  };

  const handleEdit = (template: FolderTemplate) => {
    setEditingId(template.id);
    setIsEditorOpen(true);
  };

  const handleDelete = (template: FolderTemplate) => {
    if (
      !confirm(
        `Delete template "${template.name}"? Contacts using it will be detached. ` +
          `Existing client folders and documents are not affected.`,
      )
    ) {
      return;
    }
    deleteMutation.mutate(template.id, {
      onSuccess: () => toast({ variant: 'success', title: `"${template.name}" deleted` }),
      onError: () => toast({ variant: 'error', title: 'Failed to delete template' }),
    });
  };

  const rows = templates ?? [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Folder Templates"
          description="Reusable document folder structures. Assign one to a contact to organize their documents consistently across the back office, object storage, and the client portal."
          action={
            canManage ? (
              <Button size="sm" onClick={handleCreate}>
                Add Template
              </Button>
            ) : undefined
          }
        />
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
                Loading templates...
              </div>
            </div>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No folder templates yet. Click &quot;Add Template&quot; to define one.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-border-dark dark:text-gray-400">
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2">Default</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border dark:divide-border-dark">
                  {rows.map((template) => (
                    <tr
                      key={template.id}
                      className="hover:bg-surface-secondary dark:hover:bg-surface-dark-tertiary transition-colors"
                    >
                      <td className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                        {template.name}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">
                        {template.description || '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        {template.isDefault ? (
                          <Badge variant="info" size="sm">
                            Default
                          </Badge>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge variant={template.isActive ? 'success' : 'default'} size="sm">
                          {template.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canManage && (
                            <Button size="sm" variant="ghost" onClick={() => handleEdit(template)}>
                              Edit
                            </Button>
                          )}
                          {canManage && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(template)}
                              className="text-danger-600 hover:text-danger-700"
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {isEditorOpen && (
        <TemplateEditorModal
          templateId={editingId}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingId(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Editor Modal ─────────────────────────────────────────────────────────────

/** Editable tree node with a stable key for React list rendering. */
interface EditableNode {
  key: string;
  name: string;
  isDefault: boolean;
  children: EditableNode[];
}

let keyCounter = 0;
const nextKey = () => `node-${keyCounter++}`;

function toEditable(nodes: FolderTemplateTreeNode[]): EditableNode[] {
  return nodes.map((n) => ({
    key: nextKey(),
    name: n.name,
    isDefault: n.isDefault ?? false,
    children: toEditable(n.children ?? []),
  }));
}

function toPayload(nodes: EditableNode[]): FolderTemplateNodePayload[] {
  return nodes
    .filter((n) => n.name.trim().length > 0)
    .map((n) => ({
      name: n.name.trim(),
      is_default: n.isDefault,
      children: toPayload(n.children),
    }));
}

/** Clears is_default on every node except the one with the given key. */
function setDefaultKey(nodes: EditableNode[], key: string): EditableNode[] {
  return nodes.map((n) => ({
    ...n,
    isDefault: n.key === key,
    children: setDefaultKey(n.children, key),
  }));
}

function TemplateEditorModal({
  templateId,
  onClose,
}: {
  templateId: string | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const isEditing = !!templateId;
  const { data: existing, isLoading } = useFolderTemplate(templateId ?? '');

  const createMutation = useCreateFolderTemplate();
  const updateMutation = useUpdateFolderTemplate(templateId ?? '');

  // Local editor state, hydrated from the loaded template.
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [nodes, setNodes] = useState<EditableNode[]>([]);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate once the template detail arrives (edit mode) or immediately (create).
  if (!hydrated) {
    if (isEditing && existing) {
      setName(existing.name);
      setDescription(existing.description ?? '');
      setIsDefault(existing.isDefault);
      setIsActive(existing.isActive);
      setNodes(toEditable(existing.nodes));
      setHydrated(true);
    } else if (!isEditing) {
      setHydrated(true);
    }
  }

  const addRoot = () =>
    setNodes((prev) => [...prev, { key: nextKey(), name: '', isDefault: false, children: [] }]);

  const handleSetDefault = (key: string) => setNodes((prev) => setDefaultKey(prev, key));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({ variant: 'error', title: 'Name is required' });
      return;
    }

    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      is_default: isDefault,
      is_active: isActive,
      nodes: toPayload(nodes),
    };

    const onSuccess = () => {
      toast({ variant: 'success', title: `"${name.trim()}" ${isEditing ? 'updated' : 'created'}` });
      onClose();
    };
    const onError = () =>
      toast({ variant: 'error', title: `Failed to ${isEditing ? 'update' : 'create'} template` });

    if (isEditing) {
      updateMutation.mutate(payload, { onSuccess, onError });
    } else {
      createMutation.mutate(payload, { onSuccess, onError });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={isEditing ? 'Edit Folder Template' : 'New Folder Template'}
      size="lg"
    >
      {isEditing && isLoading ? (
        <p className="py-6 text-center text-sm text-gray-500">Loading template...</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g., Standard Tax Client"
            maxLength={200}
          />
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description (optional)"
            maxLength={500}
          />

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-brand-600"
              />
              Default (applied to new contacts)
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-brand-600"
              />
              Active
            </label>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Folder structure
              </label>
              <Button type="button" size="sm" variant="outline" onClick={addRoot}>
                Add folder
              </Button>
            </div>
            <div className="rounded-lg border border-border p-3 dark:border-border-dark">
              {nodes.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No folders yet. Click &quot;Add folder&quot; to build the structure.
                </p>
              ) : (
                <NodeEditorList
                  nodes={nodes}
                  onChange={setNodes}
                  onSetDefault={handleSetDefault}
                  depth={0}
                />
              )}
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Mark one folder as the default upload target. New documents for a contact using this
                template land there unless another folder is chosen.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isPending}>
              {isEditing ? 'Save Changes' : 'Create'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

// ─── Recursive node editor ──────────────────────────────────────────────────

function NodeEditorList({
  nodes,
  onChange,
  onSetDefault,
  depth,
}: {
  nodes: EditableNode[];
  onChange: (nodes: EditableNode[]) => void;
  onSetDefault: (key: string) => void;
  depth: number;
}) {
  const updateNode = (key: string, updated: EditableNode) =>
    onChange(nodes.map((n) => (n.key === key ? updated : n)));

  const removeNode = (key: string) => onChange(nodes.filter((n) => n.key !== key));

  const addChild = (key: string) =>
    onChange(
      nodes.map((n) =>
        n.key === key
          ? {
              ...n,
              children: [
                ...n.children,
                { key: nextKey(), name: '', isDefault: false, children: [] },
              ],
            }
          : n,
      ),
    );

  return (
    <ul className="space-y-2" role="list">
      {nodes.map((node) => (
        <li key={node.key}>
          <div className="flex items-center gap-2">
            <span className="text-gray-400" aria-hidden="true">
              📁
            </span>
            <Input
              value={node.name}
              onChange={(e) => updateNode(node.key, { ...node, name: e.target.value })}
              placeholder="Folder name"
              maxLength={200}
              className="flex-1"
              aria-label={`Folder name at depth ${depth + 1}`}
            />
            <button
              type="button"
              onClick={() => onSetDefault(node.key)}
              className={`shrink-0 rounded px-2 py-1 text-xs font-medium ${
                node.isDefault
                  ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                  : 'text-gray-400 hover:text-brand-600 dark:hover:text-brand-400'
              }`}
              title="Set as the default upload target"
              aria-pressed={node.isDefault}
            >
              {node.isDefault ? '★ Default' : '☆ Default'}
            </button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => addChild(node.key)}
              title="Add subfolder"
            >
              + Sub
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => removeNode(node.key)}
              className="text-danger-600 hover:text-danger-700"
              title="Remove folder"
            >
              Remove
            </Button>
          </div>
          {node.children.length > 0 && (
            <div className="ml-6 mt-2 border-l border-border pl-3 dark:border-border-dark">
              <NodeEditorList
                nodes={node.children}
                onChange={(children) => updateNode(node.key, { ...node, children })}
                onSetDefault={onSetDefault}
                depth={depth + 1}
              />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
